import {
	type AdminEpisodeDetail,
	type AdminGoldenAsset,
	type AdminProductionProgress,
	type AdminSeriesDetail,
	type GenerationFailureReason,
	type GenerationJob,
	GenerationJobStatus,
	type GenerationKind,
	GenerationTargetType,
	type ProductionState,
	ProductionState as ProductionStateEnum,
	type StoryGraphIssue,
	StoryGraphIssueSchema,
} from "@storyline/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { HttpError } from "@/src/common/services/api-error";
import { createGeneration, listStoryGenerations, refreshGeneration, retryGeneration } from "@/src/common/services/generation-http.service";
import { listAssets } from "@/src/series/services/assets-http.service";
import { getSeries } from "@/src/series/services/series-http.service";
import {
	deleteEpisode,
	getEpisode,
	getProduction,
	publishEpisode,
	transitionProduction,
	unpublishEpisode,
	uploadEpisodeCover,
} from "@/src/stories/services/episodes-http.service";

const POLL_INTERVAL_MS = 3000;
const GENERATING_STATES = new Set<ProductionState>([ProductionStateEnum.KEYFRAMES_GENERATING, ProductionStateEnum.VIDEOS_GENERATING]);

const isActive = (job: GenerationJob): boolean =>
	job.status === GenerationJobStatus.PENDING || job.status === GenerationJobStatus.PROCESSING;

const parseIssues = (details: Record<string, unknown> | null): StoryGraphIssue[] => {
	if (!details || !Array.isArray(details.issues)) {
		return [];
	}
	const parsed: StoryGraphIssue[] = [];
	for (const raw of details.issues) {
		const result = StoryGraphIssueSchema.safeParse(raw);
		if (result.success) {
			parsed.push(result.data);
		}
	}
	return parsed;
};

export const useEpisode = (episodeId: string, seriesId: string) => {
	const [episode, setEpisode] = useState<AdminEpisodeDetail | null>(null);
	const [series, setSeries] = useState<AdminSeriesDetail | null>(null);
	const [assets, setAssets] = useState<AdminGoldenAsset[]>([]);
	const [generations, setGenerations] = useState<GenerationJob[]>([]);
	const [production, setProduction] = useState<AdminProductionProgress | null>(null);
	const [issues, setIssues] = useState<StoryGraphIssue[]>([]);
	const [transitionError, setTransitionError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const pollingRef = useRef(false);

	const reload = useCallback(async () => {
		try {
			const [nextEpisode, nextSeries, nextAssets, nextGenerations, nextProduction] = await Promise.all([
				getEpisode(episodeId),
				getSeries(seriesId),
				listAssets(seriesId),
				listStoryGenerations(episodeId),
				getProduction(episodeId),
			]);
			setEpisode(nextEpisode);
			setSeries(nextSeries);
			setAssets(nextAssets.assets);
			setGenerations(nextGenerations.jobs);
			setProduction(nextProduction);
			setHasError(false);
		} catch {
			setHasError(true);
		} finally {
			setIsLoading(false);
		}
	}, [episodeId, seriesId]);

	useEffect(() => {
		setIsLoading(true);
		void reload();
	}, [reload]);

	const generating = production ? GENERATING_STATES.has(production.productionState) : false;

	useEffect(() => {
		if (!generating) {
			return;
		}
		const timer = setInterval(async () => {
			if (pollingRef.current) {
				return;
			}
			pollingRef.current = true;
			try {
				const active = generations.filter(isActive).map((job) => job.id);
				await Promise.all(active.map((id) => refreshGeneration(id).catch(() => null)));
				await reload();
			} finally {
				pollingRef.current = false;
			}
		}, POLL_INTERVAL_MS);
		return () => clearInterval(timer);
	}, [generating, generations, reload]);

	const transition = useCallback(
		async (to: ProductionState): Promise<void> => {
			setTransitionError(null);
			setIssues([]);
			try {
				const next = await transitionProduction(episodeId, to);
				setProduction(next);
				await reload();
			} catch (caught) {
				if (caught instanceof HttpError) {
					setTransitionError(caught.message);
					setIssues(parseIssues(caught.details));
				} else {
					setTransitionError("La transition a échoué.");
				}
			}
		},
		[episodeId, reload],
	);

	const latestTerminalJob = useCallback(
		(shotId: string, kind: GenerationKind): GenerationJob | null => {
			const matching = generations
				.filter((job) => job.targetId === shotId && job.kind === kind && !isActive(job))
				.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
			return matching[0] ?? null;
		},
		[generations],
	);

	const generateFrame = useCallback(
		async (shotId: string, kind: GenerationKind): Promise<void> => {
			await createGeneration({ kind, storyId: episodeId, seriesId, targetType: GenerationTargetType.SHOT, targetId: shotId });
			await reload();
		},
		[episodeId, seriesId, reload],
	);

	const regenerateFrame = useCallback(
		async (shotId: string, kind: GenerationKind, failureReason: GenerationFailureReason): Promise<void> => {
			const previous = latestTerminalJob(shotId, kind);
			if (previous) {
				await retryGeneration(previous.id, { failureReason });
			} else {
				await createGeneration({ kind, storyId: episodeId, seriesId, targetType: GenerationTargetType.SHOT, targetId: shotId });
			}
			await reload();
		},
		[episodeId, seriesId, latestTerminalJob, reload],
	);

	const publish = useCallback(async (): Promise<void> => {
		setTransitionError(null);
		try {
			await publishEpisode(episodeId);
			await reload();
		} catch (caught) {
			if (caught instanceof HttpError) {
				setTransitionError(caught.message);
				setIssues(parseIssues(caught.details));
			}
		}
	}, [episodeId, reload]);

	const unpublish = useCallback(async (): Promise<void> => {
		await unpublishEpisode(episodeId);
		await reload();
	}, [episodeId, reload]);

	const uploadCover = useCallback(
		async (file: File): Promise<void> => {
			await uploadEpisodeCover(episodeId, file);
			await reload();
		},
		[episodeId, reload],
	);

	const remove = useCallback(() => deleteEpisode(episodeId), [episodeId]);

	return {
		episode,
		series,
		assets,
		generations,
		production,
		issues,
		transitionError,
		isLoading,
		hasError,
		reload,
		transition,
		generateFrame,
		regenerateFrame,
		publish,
		unpublish,
		uploadCover,
		remove,
	};
};
